import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { type Adult, adultSchema } from "@/schemas/registration-schema";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetClose } from "@/components/ui/sheet";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, User, Mail, Phone, Pencil, X } from "lucide-react";
import { useEffect, useState } from "react";
import { AsYouType } from "libphonenumber-js";
import type { AdultBase } from "nautika-types";

interface AdultFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdultCreated: (adult: AdultBase) => void;
  editingAdult?: AdultBase; // Prop optionnelle pour l'édition
  mode?: "create" | "view" | "edit";
}

export function AdultForm({ open, onOpenChange, onAdultCreated, editingAdult, mode = "create" }: AdultFormProps) {
  // Only track a user override to switch to edit, scoped to the current adult id.
  const [modeOverride, setModeOverride] = useState<{
    adultId: string;
    mode: "edit";
  } | null>(null);
  const form = useForm<Adult>({
    resolver: zodResolver(adultSchema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: editingAdult?.firstName || "",
      lastName: editingAdult?.lastName || "",
      email: editingAdult?.email || "",
      phone: editingAdult?.phone || "",
    },
  });

  // Réinitialiser le formulaire quand editingAdult change
  useEffect(() => {
    if (editingAdult) {
      form.reset({
        firstName: editingAdult.firstName,
        lastName: editingAdult.lastName,
        email: editingAdult.email || "",
        phone: editingAdult.phone || "",
      });
    } else {
      form.reset({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
      });
    }
  }, [editingAdult, form]);

  // Compute the effective form mode from props and the optional user override.
  const baseMode: "create" | "view" | "edit" = editingAdult ? mode : "create";
  const formMode: "create" | "view" | "edit" = modeOverride && editingAdult && modeOverride.adultId === editingAdult.id ? modeOverride.mode : baseMode;

  // Ensure local override is cleared when the sheet closes, so reopening starts fresh.
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setModeOverride(null);
    onOpenChange(nextOpen);
  };

  const createMutation = api.useMutation("post", "/registrations/adults/", {
    onSuccess: (response) => {
      onAdultCreated(response as AdultBase);
      form.reset();
      onOpenChange(false);
      toast.success("Adulte créé avec succès");
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de la création");
    },
  });

  const updateMutation = api.useMutation("put", "/registrations/adults/{id}", {
    onSuccess: (response) => {
      onAdultCreated(response as AdultBase);
      form.reset();
      onOpenChange(false);
      toast.success("Adulte modifié avec succès");
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de la modification");
    },
  });

  const onSubmit = (data: Adult) => {
    // Transformer les chaînes vides en undefined pour l'API
    const transformedData = {
      ...data,
      email: data.email === "" ? undefined : data.email,
      phone: data.phone === "" ? undefined : data.phone,
    };
    if (formMode === "view") return; // lecture seule
    if (editingAdult && formMode === "edit") {
      updateMutation.mutate({
        params: { path: { id: editingAdult.id } },
        body: transformedData,
      });
    } else {
      createMutation.mutate({ body: transformedData });
    }
  };

  const asYouType = new AsYouType({
    defaultCountry: "FR",
    defaultCallingCode: "33",
  });

  const isReadOnly = formMode === "view" || createMutation.isPending || updateMutation.isPending;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="h-[100vh] !max-w-md overflow-y-auto [&>button]:hidden">
        <SheetHeader className="sticky top-0 z-2 border-b bg-white shadow-xs">
          <div className="flex items-center justify-between gap-4">
            <SheetTitle>
              {formMode === "view" && editingAdult && `Fiche ${editingAdult.firstName} ${editingAdult.lastName}`}
              {formMode === "edit" && editingAdult && `Modifier ${editingAdult.firstName}`}
              {formMode === "create" && "Nouvel adulte"}
            </SheetTitle>
            <div className="flex items-center gap-2">
              {formMode === "view" && editingAdult && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setModeOverride({ adultId: editingAdult.id, mode: "edit" })}>
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Modifier
                  </Button>
                  <SheetClose asChild>
                    <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Fermer">
                      <X className="h-4 w-4" />
                    </Button>
                  </SheetClose>
                </>
              )}
              {formMode === "edit" && editingAdult && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    // Reset values and go back to view if possible
                    form.reset({
                      firstName: editingAdult.firstName,
                      lastName: editingAdult.lastName,
                      email: editingAdult.email || "",
                      phone: editingAdult.phone || "",
                    });
                    // If edit comes from a local override, cancel it; otherwise, close the sheet
                    if (modeOverride && modeOverride.adultId === editingAdult.id) {
                      setModeOverride(null);
                    } else {
                      onOpenChange(false);
                    }
                  }}
                >
                  <X className="mr-2 h-3.5 w-3.5" /> Annuler
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4 px-8">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <User className="mr-2 inline h-4 w-4" />
                    Prénom <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Prénom" {...field} disabled={isReadOnly} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <User className="mr-2 inline h-4 w-4" />
                    Nom <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Nom" {...field} disabled={isReadOnly} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Phone field above email */}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Phone className="mr-2 inline h-4 w-4" />
                    Téléphone
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="+33 6 12 34 56 78"
                      autoComplete="tel"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value;
                        // reset formatter for fresh input
                        asYouType.reset();
                        // format current input
                        const formatted = asYouType.input(value);
                        field.onChange(formatted);
                        // if local prefix typed, prepend country code
                        if (["01", "02", "03", "04", "05", "06", "07", "09"].includes(value.slice(0, 2))) {
                          asYouType.reset();
                          field.onChange(asYouType.input("+33" + value.slice(1)));
                        }
                      }}
                      disabled={isReadOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Email field moved below phone */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Mail className="mr-2 inline h-4 w-4" />
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="adresse@mail.com" {...field} disabled={isReadOnly} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <SheetFooter className="px-8">
              {formMode !== "view" && (
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {createMutation.isPending || updateMutation.isPending
                    ? editingAdult
                      ? "Modification..."
                      : "Création..."
                    : editingAdult
                      ? "Enregistrer les modifications"
                      : "Ajouter cet adulte"}
                </Button>
              )}
              {formMode !== "view" && (
                <SheetClose asChild>
                  <Button variant="outline">Annuler</Button>
                </SheetClose>
              )}
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
